import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { AppLayout } from "@/components/intel/Layout";
import { ModuleShell } from "@/components/intel/ModuleShell";
import { PublicContactsPanel } from "@/components/intel/PublicContactsPanel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { runOrgContactIntel, searchPublicContacts, type ContactIndexRow } from "@/lib/osint/contact/contact.functions";
import { Building2, ExternalLink, Loader2, MapPin, Phone, Search, Mail } from "lucide-react";

export const Route = createFileRoute("/contacts")({
  head: () => ({
    meta: [
      { title: "Organization Contacts & Contact Search · CARIO Nexus" },
      {
        name: "description",
        content:
          "Discover published business addresses, switchboards and professional contact points from official registries and organisation websites, then search back from a phone number, address or organisation.",
      },
      { property: "og:title", content: "Organization Contacts & Contact Search · CARIO Nexus" },
      {
        property: "og:description",
        content: "Public professional contact intelligence with registry provenance, context labels and evidence.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: ContactsPage,
});

const KIND_ICON = { phone: Phone, address: MapPin, email: Mail } as const;

function ResultRow({ row }: { row: ContactIndexRow }) {
  const Icon = KIND_ICON[row.kind];
  return (
    <li className="rounded-md border border-panel-border bg-background/40 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-cyan" />
        <span className="break-all text-sm">{row.value}</span>
        <Badge variant="outline" className="font-mono text-[10px]">
          {row.contextLabel ?? "UNKNOWN"}
        </Badge>
        <Badge variant="outline" className="font-mono text-[10px]">
          {Math.round(row.confidence * 100)}%
        </Badge>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span className="font-mono uppercase tracking-wider">{row.subjectType}</span>
        <span className="truncate">{row.subject}</span>
        {row.organization && <span className="truncate">· {row.organization}</span>}
        {row.sourceUrl && (
          <a href={row.sourceUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-cyan hover:underline">
            {row.sourceLabel ?? "source"} <ExternalLink className="h-3 w-3" />
          </a>
        )}
        <span>
          first seen {new Date(row.firstSeen).toLocaleDateString()} · last seen {new Date(row.lastSeen).toLocaleDateString()}
        </span>
      </div>
    </li>
  );
}

function ContactsPage() {
  const [org, setOrg] = useState("");
  const [domain, setDomain] = useState("");
  const [country, setCountry] = useState("");
  const [query, setQuery] = useState("");

  const orgFn = useServerFn(runOrgContactIntel);
  const searchFn = useServerFn(searchPublicContacts);

  const orgRun = useMutation({
    mutationFn: (input: { organization: string; domain?: string; country?: string }) => orgFn({ data: input }),
  });
  const lookup = useMutation({
    mutationFn: (q: string) => searchFn({ data: { query: q, mode: "auto" as const } }),
  });

  return (
    <AppLayout>
      <ModuleShell
        eyebrow="Contact intelligence"
        title="Organization contacts & contact search"
        description="Published professional contact information only — registry records, official organisation pages and public documents, each with its source and context."
      >
        <Tabs defaultValue="organization">
          <TabsList>
            <TabsTrigger value="organization" className="text-xs">
              Organization contacts
            </TabsTrigger>
            <TabsTrigger value="search" className="text-xs">
              Contact search
            </TabsTrigger>
          </TabsList>

          <TabsContent value="organization" className="mt-3 space-y-3">
            <section className="glass rounded-xl p-3 sm:p-4">
              <form
                className="grid gap-2 sm:grid-cols-[2fr_1.4fr_1fr_auto]"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (org.trim().length < 2) return;
                  orgRun.mutate({
                    organization: org.trim(),
                    ...(domain.trim() ? { domain: domain.trim() } : {}),
                    ...(country.trim() ? { country: country.trim() } : {}),
                  });
                }}
              >
                <Input value={org} onChange={(e) => setOrg(e.target.value)} placeholder="Organization name or business ID" />
                <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="Official website (optional)" />
                <Input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Country (optional)" />
                <Button type="submit" disabled={orgRun.isPending}>
                  {orgRun.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Building2 className="h-4 w-4" />}
                  <span className="ml-1.5">Run</span>
                </Button>
              </form>
              {orgRun.isPending && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Querying public registries, the official site and published documents…
                </p>
              )}
              {orgRun.isError && (
                <p className="mt-2 text-xs text-destructive">{(orgRun.error as Error).message}</p>
              )}
            </section>

            {orgRun.data && <PublicContactsPanel result={orgRun.data.result} />}
          </TabsContent>

          <TabsContent value="search" className="mt-3 space-y-3">
            <section className="glass rounded-xl p-3 sm:p-4">
              <form
                className="flex gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (query.trim().length < 2) return;
                  lookup.mutate(query.trim());
                }}
              >
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Phone number, street address, e-mail or organisation"
                />
                <Button type="submit" disabled={lookup.isPending}>
                  {lookup.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  <span className="ml-1.5">Search</span>
                </Button>
              </form>
              <p className="mt-2 text-xs text-muted-foreground">
                Searches the public contact records already collected by your investigations — matching people and
                organisations back from a number, an address or an organisation name.
              </p>
            </section>

            {lookup.data && (
              <section className="glass rounded-xl p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-display text-sm">
                    {lookup.data.rows.length} match{lookup.data.rows.length === 1 ? "" : "es"}
                  </h2>
                  <Badge variant="outline" className="font-mono text-[10px] uppercase">
                    interpreted as {lookup.data.mode}
                  </Badge>
                </div>
                {lookup.data.rows.length ? (
                  <ul className="mt-3 space-y-2">
                    {lookup.data.rows.map((r) => (
                      <ResultRow key={r.id} row={r} />
                    ))}
                  </ul>
                ) : (
                  <p className="mt-3 rounded-md border border-dashed border-panel-border p-4 text-center text-xs text-muted-foreground">
                    No stored public contact record matches this. Run a person or organisation investigation first — every
                    discovered contact is indexed here automatically.
                  </p>
                )}
              </section>
            )}
          </TabsContent>
        </Tabs>
      </ModuleShell>
    </AppLayout>
  );
}
